import type { IFUserInfo } from "../../../api/apiRsp/IFSigninRsp";
import {
  getCurrentChannel,
  getCurrentCountryCode,
} from "../../../utils/countryConfigUtils";
import { getResolvedPid } from "../../attribution/runtime";
import { KOL_EM_PROPERTY_KEYS } from "../../attribution/kolVisit";
import { getInitialEmSuperProperties, getEmRuntimeContext } from "./context";
import { getHallAcquisitionContext } from "./hallAcquisition";
import type { HallAcquisitionContext } from "./hallAcquisition";
import type { EmEventName, EmTrackProps } from "./events";
import { buildEmAccountId, buildEmUserProperties } from "./identity";

let sdkInitialized = false;
let currentEmAccountId: string | null = null;
const pendingSdkCalls = new Set<() => void>();
let removeSdkLoadListeners: (() => void) | undefined;

const getSdkScript = (): HTMLScriptElement | null =>
  typeof document === "undefined" ? null : document.querySelector("#engageminds-sdk");

const watchSdkLoad = (): void => {
  const script = getSdkScript();
  if (!script || removeSdkLoadListeners || script.dataset.emLoadState === "failed") return;
  const onLoad = () => {
    removeSdkLoadListeners?.();
    removeSdkLoadListeners = undefined;
    if (!getEmSdk()) script.dataset.emLoadState = "failed";
    if (!initEmSdk()) pendingSdkCalls.clear();
  };
  const onError = () => {
    script.dataset.emLoadState = "failed";
    pendingSdkCalls.clear();
    removeSdkLoadListeners?.();
    removeSdkLoadListeners = undefined;
  };
  script.addEventListener("load", onLoad, { once: true });
  script.addEventListener("error", onError, { once: true });
  removeSdkLoadListeners = () => {
    script.removeEventListener("load", onLoad);
    script.removeEventListener("error", onError);
  };
};

export const whenEmSdkReady = (callback: () => void): (() => void) => {
  const script = getSdkScript();
  if (initEmSdk()) {
    callback();
  } else if (
    !getEmSdk() &&
    getResolvedEmAppKey() &&
    script && script.dataset.emLoadState !== "failed"
  ) {
    pendingSdkCalls.add(callback);
  }
  return () => { pendingSdkCalls.delete(callback); };
};

const shouldSuppressEmSdkWarn = (args: unknown[]): boolean => {
  const [message, propertyName] = args;

  return (
    typeof message === "string" &&
    message.includes("InvalidPropertyName") &&
    (propertyName === "#browser" || propertyName === "#browserv")
  );
};

const runWithSuppressedEmSdkWarn = <T>(callback: () => T): T => {
  if (typeof window === "undefined") {
    return callback();
  }

  const originalWarn = window.console.warn;
  window.console.warn = (...args: Parameters<typeof console.warn>) => {
    if (shouldSuppressEmSdkWarn(args)) {
      return;
    }

    originalWarn(...args);
  };

  try {
    return callback();
  } finally {
    window.console.warn = originalWarn;
  }
};

const getFallbackRuntimeContext = (): EmTrackProps => {
  const bootstrap =
    typeof window !== "undefined" ? window.__BOOTSTRAP_ATTR__ : undefined;
  const host =
    typeof window !== "undefined" ? window.location.host || "" : "";
  const currentPath =
    typeof window !== "undefined"
      ? `${window.location.pathname || ""}${window.location.search || ""}`
      : "";

  return {
    countryCode:
      bootstrap?.countryCode?.toLowerCase() ||
      (getCurrentCountryCode() || "").toLowerCase(),
    channel: getCurrentChannel(),
    pid: getResolvedPid(),
    currentHost: host,
    host,
    currentPath,
    isLogin:
      typeof window !== "undefined"
        ? !!window.localStorage.getItem("authToken")
        : false,
    ...getHallAcquisitionContext(),
  };
};

const getSafeInitialSuperProperties = (): EmTrackProps => {
  return sanitizeTrackProps({
    ...getFallbackRuntimeContext(),
    ...(getInitialEmSuperProperties() || {}),
  });
};

const getSafeRuntimeContext = (): EmTrackProps => {
  return sanitizeTrackProps({
    ...getFallbackRuntimeContext(),
    ...(getEmRuntimeContext() || {}),
  });
};

const getEmSdk = (): Window["EmSdk"] | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window.EmSdk;
};

const sanitizeTrackProps = (props: EmTrackProps = {}): EmTrackProps => {
  const sanitized: EmTrackProps = {};

  Object.entries(props).forEach(([key, value]) => {
    if (value !== undefined) {
      sanitized[key] = value;
    }
  });

  return sanitized;
};

const syncAcquisitionSuperProperties = (context: HallAcquisitionContext): void => {
  const sdk = getEmSdk();
  if (!sdk?.getSuperProperties || !sdk.unsetSuperProperty || !sdk.setSuperProperties) {
    return;
  }

  // SDK request-level bps is separate from the callback's event props.
  const current = sdk.getSuperProperties();
  const changed: Record<string, string> = {};
  for (const key of KOL_EM_PROPERTY_KEYS) {
    const value = context[key];
    if (value === undefined && Object.prototype.hasOwnProperty.call(current, key)) {
      sdk.unsetSuperProperty(key);
    } else if (value !== undefined && value !== current[key]) {
      changed[key] = value;
    }
  }
  if (Object.keys(changed).length) {
    sdk.setSuperProperties(changed);
  }
};

const setLoginSuperProperty = (isLogin: boolean): void => {
  const sdk = getEmSdk();
  if (!sdkInitialized || !sdk?.setSuperProperties) {
    return;
  }

  sdk.setSuperProperties({
    ...getInitialEmSuperProperties(),
    isLogin,
  });
};

export const getResolvedEmAppKey = (): string => {
  if (typeof window !== "undefined") {
    return window.__BOOTSTRAP_ATTR__?.emAppKey?.trim() || "";
  }

  return "";
};

export const initEmSdk = (): boolean => {
  if (sdkInitialized) {
    return true;
  }

  const sdk = getEmSdk();
  const appKey = getResolvedEmAppKey();

  if (!sdk || !appKey) {
    if (!sdk && appKey) watchSdkLoad();
    return false;
  }

  try {
    const superProperties = getSafeInitialSuperProperties();
    runWithSuppressedEmSdkWarn(() =>
      sdk.init({
        appk: appKey,
        autoTrack: {
          pageHide: true,
          pageShow: true,
        },
        superProperties: { ...superProperties },
        trackEventCallback(_eventId, props) {
          const context = getHallAcquisitionContext();
          syncAcquisitionSuperProperties(context);
          for (const key of KOL_EM_PROPERTY_KEYS) {
            delete props[key];
          }
          Object.assign(props, context);
          return true;
        },
      }),
    );
    // SDK init merges its persisted supers over the supplied object.
    syncAcquisitionSuperProperties(getHallAcquisitionContext());
    sdk.setSuperProperties?.(superProperties);
    sdkInitialized = true;
  } catch (error) {
    console.warn("Failed to initialize EngageMinds SDK", error);
    return false;
  }
  const pending = Array.from(pendingSdkCalls);
  pendingSdkCalls.clear();
  pending.forEach((callback) => callback());
  return true;
};

export const trackEmEvent = (eventName: EmEventName, props: EmTrackProps = {}): void => {
  const eventProps = sanitizeTrackProps({ ...getSafeRuntimeContext(), ...props });
  whenEmSdkReady(() => getEmSdk()?.track?.(eventName, eventProps));
};

export const loginEmUser = (
  userId?: string | number | null,
  countryCode?: string | null,
): void => {
  const accountId = buildEmAccountId(
    countryCode || getCurrentCountryCode(),
    userId,
  );

  if (!accountId) return;
  whenEmSdkReady(() => {
    const sdk = getEmSdk();
    if (!sdk?.login || currentEmAccountId === accountId) return;
    syncAcquisitionSuperProperties(getHallAcquisitionContext());
    sdk.login(accountId);
    currentEmAccountId = accountId;
    setLoginSuperProperty(true);
  });
};

export const logoutEmUser = (): void => {
  whenEmSdkReady(() => {
    const sdk = getEmSdk();
    syncAcquisitionSuperProperties(getHallAcquisitionContext());
    sdk?.logout?.();
    currentEmAccountId = null;
    setLoginSuperProperty(false);
  });
};

export const setEmUserProperties = (properties: Record<string, unknown>): void => {
  const snapshot = { ...properties };
  whenEmSdkReady(() => {
    syncAcquisitionSuperProperties(getHallAcquisitionContext());
    getEmSdk()?.userSet?.(snapshot);
  });
};

export const syncEmUserFromAuth = (
  userId?: string | number | null,
  userInfo?: Partial<IFUserInfo> | null,
): void => {
  const countryCode = userInfo?.countryCode || getCurrentCountryCode();
  loginEmUser(userId, countryCode);
  setEmUserProperties(
    buildEmUserProperties(userInfo, {
      countryCode,
      channel: getCurrentChannel(),
      pid: getResolvedPid(),
    }),
  );
};

export const flushEmEvents = (): void => {
  whenEmSdkReady(() => {
    syncAcquisitionSuperProperties(getHallAcquisitionContext());
    getEmSdk()?.flush?.();
  });
};

export const resetEmSdkForTests = (): void => {
  sdkInitialized = false;
  currentEmAccountId = null;
  pendingSdkCalls.clear();
  removeSdkLoadListeners?.();
  removeSdkLoadListeners = undefined;
};
